import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';

/**
 * Roolien käyttöoikeudet: the per-organization permission matrix (organizations/permission_registry.py).
 *
 * Reads and writes GET/POST /api/organizations/{id}/role-permissions/. Editable keys render as
 * checkboxes per role; the admin column is locked; the rest of the registry is listed read-only.
 * The draft is saved with explicit Save/Cancel, one POST per changed role, then /users/me is
 * refetched so the admin's own gates update at once.
 */
export default function PermissionMatrixSection({ onDirtyChange }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentOrganization, fetchUser } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null); // { role: Set(editable keys granted) }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mobileRole, setMobileRole] = useState('employee');
  const [othersOpen, setOthersOpen] = useState(false);
  const [resetRole, setResetRole] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const url = `/api/organizations/${orgId}/role-permissions/`;
  const requestConfig = useMemo(
    () => ({
      params: { organization: orgId },
      headers: { 'X-CSRFToken': getCsrfToken(), 'X-Organization-Id': orgId },
    }),
    [orgId]
  );

  const draftFrom = (payload) => {
    const editable = payload.registry.filter((row) => row.editable).map((row) => row.key);
    const next = {};
    payload.roles.forEach((role) => {
      const effective = new Set(payload.effective[role] || []);
      next[role] = new Set(editable.filter((key) => effective.has(key)));
    });
    return next;
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(url, requestConfig);
      setData(response.data);
      setDraft(draftFrom(response.data));
    } catch (error) {
      console.error('Failed to load role permissions:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [orgId, url, requestConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const roles = data?.roles || [];
  const lockedRoles = useMemo(() => new Set(data?.locked_roles || []), [data]);
  const editableRows = useMemo(() => (data?.registry || []).filter((row) => row.editable), [data]);
  const lockedRows = useMemo(() => (data?.registry || []).filter((row) => !row.editable), [data]);
  const areaOrder = useMemo(() => (data?.areas || []).map((area) => area.key), [data]);

  const groupByArea = (rows) => {
    const groups = new Map();
    areaOrder.forEach((area) => groups.set(area, []));
    rows.forEach((row) => {
      if (!groups.has(row.area)) groups.set(row.area, []);
      groups.get(row.area).push(row);
    });
    return [...groups.entries()].filter(([, items]) => items.length > 0);
  };

  const areaLabel = (area) => {
    const fromApi = (data?.areas || []).find((row) => row.key === area)?.label || area;
    return t(`permissions.areas.${area}`, { defaultValue: fromApi });
  };
  const keyLabel = (row) => t(`permissions.keys.${row.key}.label`, { defaultValue: row.label });
  const keyDescription = (row) =>
    row.description ? t(`permissions.keys.${row.key}.description`, { defaultValue: row.description }) : '';
  const roleLabel = (role) => t(`permissions.roles.${role}`, { defaultValue: role });

  const dirtyRoles = useMemo(() => {
    if (!data || !draft) return [];
    return roles.filter((role) => {
      if (lockedRoles.has(role)) return false;
      const saved = draftFrom(data)[role];
      const current = draft[role];
      if (saved.size !== current.size) return true;
      for (const key of current) if (!saved.has(key)) return true;
      return false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, draft, roles, lockedRoles]);
  const isDirty = dirtyRoles.length > 0;

  const toggle = (role, key) => {
    setDraft((prev) => {
      const next = { ...prev, [role]: new Set(prev[role]) };
      if (next[role].has(key)) next[role].delete(key);
      else next[role].add(key);
      return next;
    });
  };

  const handleCancel = () => {
    if (data) setDraft(draftFrom(data));
  };

  const handleSave = async () => {
    if (!data || !isDirty) return;
    setSaving(true);
    try {
      for (const role of dirtyRoles) {
        const permissions = {};
        editableRows.forEach((row) => {
          permissions[row.key] = draft[role].has(row.key);
        });
        await axios.post(url, { role, permissions }, requestConfig);
      }
      setSnackbar({ open: true, message: t('permissions.editor.saved'), severity: 'success' });
    } catch (error) {
      console.error('Failed to save role permissions:', error);
      const detail = error.response?.data?.detail;
      setSnackbar({ open: true, message: typeof detail === 'string' ? detail : t('permissions.editor.saveFailed'), severity: 'error' });
    } finally {
      // Even a partial save changed something: show the stored state and refresh the admin's gates.
      await load();
      await fetchUser();
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const role = resetRole;
    setResetRole(null);
    if (!role) return;
    setSaving(true);
    try {
      await axios.post(url, { role, reset: true }, requestConfig);
      await load();
      await fetchUser();
      setSnackbar({ open: true, message: t('permissions.editor.resetDone'), severity: 'success' });
    } catch (error) {
      console.error('Failed to reset role permissions:', error);
      setSnackbar({ open: true, message: t('permissions.editor.saveFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Report the dirty state to the settings page (tab switch and route guards).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]);

  if (!orgId) return null;

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="permission-matrix-loading">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError || !data) {
    return (
      <Alert severity="error" sx={{ mt: 4 }} data-testid="permission-matrix-error">
        {t('permissions.editor.loadFailed')}
      </Alert>
    );
  }

  const visibleRoles = isMobile ? [mobileRole] : roles;

  const renderRoleHeader = (role) => {
    const locked = lockedRoles.has(role);
    const stored = data.stored?.[role];
    const customised = Boolean(stored) && editableRows.some((row) => stored[row.key] !== row.default_roles.includes(role));
    return (
      <TableCell key={role} align="center" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {locked && <LockIcon fontSize="inherit" />}
            {roleLabel(role)}
          </Typography>
          {customised && (
            <Chip size="small" label={t('permissions.editor.modified')} data-testid={`permission-matrix-modified-${role}`} />
          )}
          {!locked && (
            <Button
              size="small"
              variant="text"
              disabled={!customised || saving}
              onClick={() => setResetRole(role)}
              data-testid={`permission-matrix-reset-${role}`}
              sx={{ fontSize: 12, textTransform: 'none' }}
            >
              {t('permissions.editor.reset')}
            </Button>
          )}
        </Box>
      </TableCell>
    );
  };

  const renderEditableCell = (role, row) => {
    const locked = lockedRoles.has(role);
    // A role the registry forbids this key for (the accountant never approves or pays
    // purchase invoices): the cell renders unchecked and disabled, the backend refuses it too.
    const forbidden = Array.isArray(row.forbidden_roles) && row.forbidden_roles.includes(role);
    const checked = locked ? true : !forbidden && draft[role].has(row.key);
    const tooltip = locked ? t('permissions.editor.locked') : forbidden ? t('permissions.editor.forbidden') : '';
    return (
      <TableCell key={role} align="center" padding="checkbox">
        <Tooltip title={tooltip} disableHoverListener={!tooltip}>
          <span>
            <Checkbox
              checked={checked}
              disabled={locked || forbidden || saving}
              onChange={() => toggle(role, row.key)}
              inputProps={{ 'aria-label': `${roleLabel(role)}: ${keyLabel(row)}`, 'data-testid': `perm-${role}-${row.key}` }}
            />
          </span>
        </Tooltip>
      </TableCell>
    );
  };

  const renderLockedCell = (role, row) => {
    const granted = (data.effective[role] || []).includes(row.key);
    return (
      <TableCell key={role} align="center" padding="checkbox" data-testid={`perm-locked-${role}-${row.key}`}>
        {granted ? <CheckIcon fontSize="small" color="action" /> : <Typography color="text.disabled">–</Typography>}
      </TableCell>
    );
  };

  const renderRows = (rows, renderCell) =>
    groupByArea(rows).map(([area, items]) => (
      <React.Fragment key={area}>
        <TableRow>
          <TableCell colSpan={1 + visibleRoles.length} sx={{ bgcolor: 'action.hover', fontWeight: 600, py: 0.75 }}>
            {areaLabel(area)}
          </TableCell>
        </TableRow>
        {items.map((row) => (
          <TableRow key={row.key} hover data-testid={`permission-row-${row.key}`}>
            <TableCell sx={{ maxWidth: 420 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {keyLabel(row)}
              </Typography>
              {keyDescription(row) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {keyDescription(row)}
                </Typography>
              )}
            </TableCell>
            {visibleRoles.map((role) => renderCell(role, row))}
          </TableRow>
        ))}
      </React.Fragment>
    ));

  return (
    <Box id="permission-matrix" data-testid="permission-matrix">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('permissions.editor.intro')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('permissions.editor.menuNote')}
      </Typography>

      {isMobile && (
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel id="permission-matrix-role-label">{t('permissions.editor.showRole')}</InputLabel>
          <Select
            labelId="permission-matrix-role-label"
            value={mobileRole}
            label={t('permissions.editor.showRole')}
            onChange={(event) => setMobileRole(event.target.value)}
            data-testid="permission-matrix-role-select"
          >
            {roles.map((role) => (
              <MenuItem key={role} value={role}>
                {roleLabel(role)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220 }}>{t('permissions.editor.roleColumn')}</TableCell>
              {visibleRoles.map(renderRoleHeader)}
            </TableRow>
          </TableHead>
          <TableBody>{renderRows(editableRows, renderEditableCell)}</TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button onClick={handleCancel} disabled={!isDirty || saving} data-testid="permission-matrix-cancel">
          {t('permissions.editor.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isDirty || saving} data-testid="permission-matrix-save">
          {saving ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : t('permissions.editor.save')}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          size="small"
          variant="text"
          startIcon={othersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setOthersOpen((open) => !open)}
          data-testid="permission-matrix-others-toggle"
          sx={{ textTransform: 'none' }}
        >
          {othersOpen ? t('permissions.editor.hideOther') : t('permissions.editor.showOther')}
        </Button>
        <Collapse in={othersOpen} unmountOnExit>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t('permissions.editor.otherHelp')}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>{t('permissions.editor.otherTitle')}</TableCell>
                  {visibleRoles.map((role) => (
                    <TableCell key={role} align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="subtitle2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <LockIcon fontSize="inherit" />
                        {roleLabel(role)}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderRows(lockedRows, renderLockedCell)}</TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>

      <Dialog open={Boolean(resetRole)} onClose={() => setResetRole(null)}>
        <DialogTitle>{t('permissions.editor.resetConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('permissions.editor.resetConfirm', { role: resetRole ? roleLabel(resetRole) : '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetRole(null)}>{t('permissions.editor.cancel')}</Button>
          <Button variant="contained" onClick={handleReset} data-testid="permission-matrix-reset-confirm">
            {t('permissions.editor.reset')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
